import { useCallback, useState } from 'react';
import { useQuota } from '../../components/quota/useQuota';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type {
  DraftVariantRead,
  GenerationRequestDetailRead,
  GenerationRequestRead,
  RejectionReason,
} from '../../types/api';
import { reviseVariant } from '../generation/generationApi';
import { MAX_REVISIONS } from './reviewLimits';

export interface RevisionFlow {
  isSubmitting: boolean;
  error: string | null;
  /** Revisions this lineage has already spent. */
  used: number;
  /**
   * Nothing left in the revision budget — by the count on the request, or
   * because the server said so on the last attempt. Unlike the rejection cap
   * this closes one action and nothing else: the caption is still editable by
   * hand and regenerating is still there.
   */
  isCapped: boolean;
  /** Sends the note and hands back the request, now `revising`. */
  submit: (feedback: string, reason: RejectionReason) => void;
  /** Clears the last message — the reviewer changed what they are asking for. */
  reset: () => void;
}

/**
 * Folds a request-level response into the detail the review page is holding.
 *
 * The revise endpoint answers with the request row (`GenerationRequestRead`),
 * not the full read, so the variants, slides and backdrops already on screen
 * stay as they are and only the row's own fields — `status`, `revisions`,
 * `updated_at` — move. A response that does carry them wins, since it is the
 * newer truth.
 */
export function withRequestFields(
  current: GenerationRequestDetailRead,
  next: GenerationRequestRead,
): GenerationRequestDetailRead {
  return {
    ...current,
    ...next,
    variants: next.variants ?? current.variants,
  };
}

/** Message for the domain errors a revision can provoke. */
function revisionMessage(caught: unknown): string {
  if (caught instanceof ApiError) {
    switch (caught.code) {
      case 'REVISION_CAP_REACHED':
        return `This post has used all ${MAX_REVISIONS} revisions. Edit the caption by hand, or reject it to start over.`;
      case 'REQUEST_NOT_EDITABLE':
        return 'This post has moved past review. Reload it to see where it stands.';
      case 'VARIANT_NOT_SELECTED':
        return 'Only the caption you chose can be revised at this step.';
      case 'VARIANT_NOT_FOUND':
        return 'That version is no longer here. Reload the post and try again.';
      case 'QUEUE_UNAVAILABLE':
        return 'We are not taking new work right now. Try again in a minute.';
      case 'QUOTA_EXCEEDED':
        return "You've used this month's posts. The counter resets at the start of next month.";
      default:
        return caught.message;
    }
  }
  return getErrorMessage(caught, 'That revision did not send. Try again.');
}

/**
 * The revision half of both review steps — the cheap way to fix a caption that
 * is nearly right.
 *
 * Unlike a rejection this is not a new request: the same row goes to `revising`
 * and comes back to the step it left from, so the caller applies the response
 * through the same path selecting a variant uses and the stream resubscribes on
 * a fresh token. One model call, the brief and the other variant untouched, and
 * at step 2 the slides re-planned on backdrops the run already drew.
 *
 * The budget is five per lineage and is the backend's to enforce
 * (`REVISION_CAP_REACHED`); the count on the request is what this hook shows
 * beforehand so the reviewer is never surprised by a 409.
 *
 * The reviewer's chip rides along as `reason`, and one value changes the work:
 * `copy_facts` has the worker fact-check the revised words again, because "this
 * is wrong" is answered with evidence rather than with a stamp saying the
 * evidence is old.
 */
export function useReviseVariant(
  businessId: string,
  /** The caption to rewrite. Null when there is none to act on yet. */
  variant: DraftVariantRead | null,
  /** `request.revisions` — what the lineage has spent so far. */
  used: number,
  onRevised: (next: GenerationRequestRead) => void,
): RevisionFlow {
  const { refresh: refreshQuota } = useQuota();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefused, setIsRefused] = useState(false);

  const reset = useCallback(() => setError(null), []);

  const submit = useCallback(
    (feedback: string, reason: RejectionReason) => {
      if (isSubmitting || variant === null) return;

      setError(null);
      setIsSubmitting(true);

      void (async () => {
        try {
          const updated = await reviseVariant(businessId, variant.request_id, variant.id, {
            feedback,
            reason,
          });
          setIsSubmitting(false);
          // A revision is a model call, so the month's allowance can move with
          // it — the header counter reads the server rather than guessing.
          refreshQuota();
          onRevised(updated);
        } catch (caught) {
          setIsSubmitting(false);
          // The cap is a state change, not a retry: the message stays inline
          // and the dialog keeps saying what the remaining ways out are.
          if (caught instanceof ApiError && caught.code === 'REVISION_CAP_REACHED') {
            setIsRefused(true);
          }
          setError(revisionMessage(caught));
        }
      })();
    },
    [businessId, isSubmitting, onRevised, refreshQuota, variant],
  );

  return {
    isSubmitting,
    error,
    used,
    isCapped: isRefused || used >= MAX_REVISIONS,
    submit,
    reset,
  };
}
